import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Layout } from "@/components/Layout";
import { TierGate } from "@/components/TierGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Trash2, Search, Skull, Target, Swords, ChevronRight, Minus,
  Save, Play, Users, Shield, Heart, Zap, AlertTriangle, ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// D&D 5e CR-to-XP mapping
const CR_XP = {
  "0": 10, "1/8": 25, "1/4": 50, "1/2": 100,
  "1": 200, "2": 450, "3": 700, "4": 1100, "5": 1800,
  "6": 2300, "7": 2900, "8": 3900, "9": 5000, "10": 5900,
  "11": 7200, "12": 8400, "13": 10000, "14": 11500, "15": 13000,
  "16": 15000, "17": 18000, "18": 20000, "19": 22000, "20": 25000,
  "21": 33000, "22": 41000, "23": 50000, "24": 62000, "25": 75000,
  "26": 90000, "27": 105000, "28": 120000, "29": 135000, "30": 155000
};

// D&D 5e XP thresholds per character level
const LEVEL_THRESHOLDS = {
  1:  { easy: 25,   medium: 50,   hard: 75,    deadly: 100 },
  2:  { easy: 50,   medium: 100,  hard: 150,   deadly: 200 },
  3:  { easy: 75,   medium: 150,  hard: 225,   deadly: 400 },
  4:  { easy: 125,  medium: 250,  hard: 375,   deadly: 500 },
  5:  { easy: 250,  medium: 500,  hard: 750,   deadly: 1100 },
  6:  { easy: 300,  medium: 600,  hard: 900,   deadly: 1400 },
  7:  { easy: 350,  medium: 750,  hard: 1100,  deadly: 1700 },
  8:  { easy: 450,  medium: 900,  hard: 1400,  deadly: 2100 },
  9:  { easy: 550,  medium: 1100, hard: 1600,  deadly: 2400 },
  10: { easy: 600,  medium: 1200, hard: 1900,  deadly: 2800 },
  11: { easy: 800,  medium: 1600, hard: 2400,  deadly: 3600 },
  12: { easy: 1000, medium: 2000, hard: 3000,  deadly: 4500 },
  13: { easy: 1100, medium: 2200, hard: 3400,  deadly: 5100 },
  14: { easy: 1250, medium: 2500, hard: 3800,  deadly: 5700 },
  15: { easy: 1400, medium: 2800, hard: 4300,  deadly: 6400 },
  16: { easy: 1600, medium: 3200, hard: 4800,  deadly: 7200 },
  17: { easy: 2000, medium: 3900, hard: 5900,  deadly: 8800 },
  18: { easy: 2100, medium: 4200, hard: 6300,  deadly: 9500 },
  19: { easy: 2400, medium: 4900, hard: 7300,  deadly: 10900 },
  20: { easy: 2800, medium: 5700, hard: 8500,  deadly: 12700 }
};

// Encounter multiplier based on number of monsters
const getMultiplier = (count, partySize) => {
  // Adjust thresholds based on party size
  let effective = count;
  if (partySize < 3) effective = Math.min(count + 1, 15); // fewer players = harder
  else if (partySize >= 6) effective = Math.max(count - 1, 1); // more players = easier

  if (effective <= 1) return 1;
  if (effective === 2) return 1.5;
  if (effective <= 6) return 2;
  if (effective <= 10) return 2.5;
  if (effective <= 14) return 3;
  return 4;
};

const getDifficulty = (adjustedXP, thresholds) => {
  if (adjustedXP >= thresholds.deadly) return "deadly";
  if (adjustedXP >= thresholds.hard) return "hard";
  if (adjustedXP >= thresholds.medium) return "medium";
  if (adjustedXP >= thresholds.easy) return "easy";
  return "trivial";
};

const DIFFICULTY_STYLES = {
  trivial: { color: "text-muted-foreground", bg: "bg-muted/30", label: "Trivial" },
  easy:    { color: "text-green-400", bg: "bg-green-900/30", label: "Easy" },
  medium:  { color: "text-yellow-400", bg: "bg-yellow-900/30", label: "Medium" },
  hard:    { color: "text-orange-400", bg: "bg-orange-900/30", label: "Hard" },
  deadly:  { color: "text-red-400", bg: "bg-red-900/30", label: "Deadly" },
};

const CREATURE_TYPES = ["Aberration", "Beast", "Celestial", "Construct", "Dragon", "Elemental", "Fey", "Fiend", "Giant", "Humanoid", "Monstrosity", "Ooze", "Plant", "Undead"];

const EncounterBuilderPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const confirm = useConfirm();

  const [campaign, setCampaign] = useState(null);
  const [creatures, setCreatures] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Current encounter being built
  const [activePlan, setActivePlan] = useState(null);
  const [planName, setPlanName] = useState("New Encounter");
  const [encounterCreatures, setEncounterCreatures] = useState([]);
  const [partySize, setPartySize] = useState(4);
  const [partyLevel, setPartyLevel] = useState(1);
  const [planNotes, setPlanNotes] = useState("");
  const [dirty, setDirty] = useState(false);

  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [bestiaryOpen, setBestiaryOpen] = useState(true);

  useEffect(() => { fetchData(); }, [campaignId]);

  // Auto-add creature from Quick Encounter URL param
  useEffect(() => {
    const addName = searchParams.get("add");
    if (addName && creatures.length > 0) {
      const match = creatures.find(c => c.name.toLowerCase() === addName.toLowerCase());
      if (match) {
        addCreature(match);
        setPlanName(`${match.name} Encounter`);
      }
    }
  }, [creatures, searchParams]);

  const fetchData = async () => {
    try {
      const [campRes, creatRes, charRes, planRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/creatures`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/characters`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/encounter-plans`, { withCredentials: true })
      ]);
      setCampaign(campRes.data);
      setCreatures(creatRes.data);
      setCharacters(charRes.data);
      setPlans(planRes.data);

      // Auto-detect party size/level from PCs
      const pcs = charRes.data.filter(c => c.character_type === "pc");
      if (pcs.length > 0) {
        setPartySize(pcs.length);
        const avgLevel = Math.round(pcs.reduce((sum, c) => sum + (c.level || 1), 0) / pcs.length);
        setPartyLevel(Math.max(1, Math.min(20, avgLevel)));
      }
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  // ==================== CALCULATIONS ====================

  const xpCalc = useMemo(() => {
    const totalMonsters = encounterCreatures.reduce((sum, c) => sum + c.quantity, 0);
    const baseXP = encounterCreatures.reduce((sum, c) => sum + (CR_XP[c.challenge_rating] || 0) * c.quantity, 0);
    const multiplier = totalMonsters > 0 ? getMultiplier(totalMonsters, partySize) : 1;
    const adjustedXP = Math.floor(baseXP * multiplier);

    const lvl = Math.max(1, Math.min(20, partyLevel));
    const perChar = LEVEL_THRESHOLDS[lvl] || LEVEL_THRESHOLDS[1];
    const thresholds = {
      easy: perChar.easy * partySize,
      medium: perChar.medium * partySize,
      hard: perChar.hard * partySize,
      deadly: perChar.deadly * partySize,
    };

    const difficulty = totalMonsters > 0 ? getDifficulty(adjustedXP, thresholds) : "trivial";

    return { baseXP, adjustedXP, multiplier, thresholds, difficulty, totalMonsters };
  }, [encounterCreatures, partySize, partyLevel]);

  // ==================== CREATURE MANAGEMENT ====================

  const addCreature = (creature) => {
    setEncounterCreatures(prev => {
      const existing = prev.find(c => c.creature_id === creature.id);
      if (existing) {
        return prev.map(c => c.creature_id === creature.id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, {
        creature_id: creature.id,
        name: creature.name,
        challenge_rating: creature.challenge_rating || "0",
        hit_points: creature.hit_points || "",
        creature_type: creature.creature_type || "",
        quantity: 1
      }];
    });
    setDirty(true);
  };

  const removeCreature = (creatureId) => {
    setEncounterCreatures(prev => prev.filter(c => c.creature_id !== creatureId));
    setDirty(true);
  };

  const updateQuantity = (creatureId, delta) => {
    setEncounterCreatures(prev => prev.map(c => {
      if (c.creature_id !== creatureId) return c;
      const newQty = Math.max(1, c.quantity + delta);
      return { ...c, quantity: newQty };
    }));
    setDirty(true);
  };

  // ==================== PLAN MANAGEMENT ====================

  const savePlan = async () => {
    try {
      const payload = {
        campaign_id: campaignId,
        name: planName,
        creatures: encounterCreatures,
        party_size: partySize,
        party_level: partyLevel,
        notes: planNotes
      };

      if (activePlan) {
        const res = await axios.put(`${API}/encounter-plans/${activePlan.id}`, payload, { withCredentials: true });
        setActivePlan(res.data);
        toast.success("Encounter plan saved");
      } else {
        const res = await axios.post(`${API}/encounter-plans`, payload, { withCredentials: true });
        setActivePlan(res.data);
        toast.success("Encounter plan created");
      }
      setDirty(false);
      setSaveDialogOpen(false);
      fetchData();
    } catch { toast.error("Failed to save plan"); }
  };

  const loadPlan = (plan) => {
    setActivePlan(plan);
    setPlanName(plan.name);
    setEncounterCreatures(plan.creatures || []);
    setPartySize(plan.party_size || 4);
    setPartyLevel(plan.party_level || 1);
    setPlanNotes(plan.notes || "");
    setDirty(false);
  };

  const deletePlan = async (id, e) => {
    e?.stopPropagation();
    const ok = await confirm("Delete Plan", "Delete this encounter plan?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/encounter-plans/${id}`, { withCredentials: true });
      toast.success("Plan deleted");
      if (activePlan?.id === id) newPlan();
      fetchData();
    } catch { toast.error("Failed to delete plan"); }
  };

  const newPlan = () => {
    setActivePlan(null);
    setPlanName("New Encounter");
    setEncounterCreatures([]);
    setPlanNotes("");
    setDirty(false);
  };

  const runInCombat = async () => {
    const planId = activePlan?.id;
    if (!planId) {
      // Save first, then run
      try {
        const payload = { campaign_id: campaignId, name: planName, creatures: encounterCreatures, party_size: partySize, party_level: partyLevel, notes: planNotes };
        const res = await axios.post(`${API}/encounter-plans`, payload, { withCredentials: true });
        await axios.post(`${API}/encounter-plans/${res.data.id}/run`, {}, { withCredentials: true });
        toast.success("Encounter launched in Combat Tracker!");
        navigate(`/campaign/${campaignId}/combat`);
      } catch { toast.error("Failed to launch encounter"); }
      return;
    }
    try {
      // Save latest changes first
      if (dirty) {
        await axios.put(`${API}/encounter-plans/${planId}`, {
          campaign_id: campaignId, name: planName, creatures: encounterCreatures,
          party_size: partySize, party_level: partyLevel, notes: planNotes
        }, { withCredentials: true });
      }
      await axios.post(`${API}/encounter-plans/${planId}/run`, {}, { withCredentials: true });
      toast.success("Encounter launched in Combat Tracker!");
      navigate(`/campaign/${campaignId}/combat`);
    } catch { toast.error("Failed to launch encounter"); }
  };

  // ==================== FILTERED CREATURES ====================

  const filteredCreatures = creatures.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === "all" || c.creature_type === filterType;
    return matchSearch && matchType;
  });

  const diffStyle = DIFFICULTY_STYLES[xpCalc.difficulty];

  if (loading) {
    return <Layout campaignName="Loading..."><div className="flex justify-center py-20"><div className="spinner" /></div></Layout>;
  }

  return (
    <Layout campaignName={campaign?.name}>
      <TierGate feature="Encounter Builder" description="Plan combat encounters with auto-calculated difficulty, creature selection, and one-click launch into the Combat Tracker.">
      <div data-testid="encounter-builder-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">Encounter Builder</h1>
            <p className="text-muted-foreground text-sm">Plan combat encounters with auto-calculated difficulty</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={newPlan} variant="outline" className="border-border rounded-sm" data-testid="new-plan-button">
              <Plus className="w-4 h-4 mr-2" /> New
            </Button>
            <Button onClick={() => setSaveDialogOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="save-plan-button">
              <Save className="w-4 h-4 mr-2" /> Save{dirty ? " *" : ""}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* ==================== SAVED PLANS SIDEBAR ==================== */}
          <div className="lg:col-span-2 space-y-2">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Saved Plans</h3>
            {plans.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No saved plans</p>
            ) : (
              plans.map(plan => (
                <Card
                  key={plan.id}
                  className={`bg-card border rounded-sm cursor-pointer entity-card ${activePlan?.id === plan.id ? "border-primary" : "border-border"}`}
                  onClick={() => loadPlan(plan)}
                  data-testid={`plan-item-${plan.id}`}
                >
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-foreground font-medium truncate block">{plan.name}</span>
                      <span className="text-xs text-muted-foreground">{(plan.creatures || []).reduce((s, c) => s + (c.quantity || 1), 0)} creatures</span>
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive flex-shrink-0" onClick={(e) => deletePlan(plan.id, e)} data-testid={`delete-plan-${plan.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* ==================== ENCOUNTER WORKSPACE ==================== */}
          <div className="lg:col-span-6">
            {/* Plan Name */}
            <Card className="bg-card border-border rounded-sm mb-4">
              <CardContent className="p-4">
                <Input
                  value={planName}
                  onChange={(e) => { setPlanName(e.target.value); setDirty(true); }}
                  placeholder="Encounter name..."
                  className="bg-transparent border-0 text-xl font-serif text-foreground p-0 h-auto focus-visible:ring-0"
                  data-testid="plan-name-input"
                />
              </CardContent>
            </Card>

            {/* Difficulty Banner */}
            <Card className={`${diffStyle.bg} border-border rounded-sm mb-4`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${diffStyle.bg}`}>
                      {xpCalc.difficulty === "deadly" ? <AlertTriangle className={`w-5 h-5 ${diffStyle.color}`} /> : <Target className={`w-5 h-5 ${diffStyle.color}`} />}
                    </div>
                    <div>
                      <span className={`text-lg font-bold ${diffStyle.color}`} data-testid="difficulty-label">{diffStyle.label}</span>
                      <p className="text-xs text-muted-foreground">
                        {xpCalc.adjustedXP.toLocaleString()} adjusted XP ({xpCalc.baseXP.toLocaleString()} base x{xpCalc.multiplier})
                      </p>
                    </div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-muted-foreground">
                      {xpCalc.totalMonsters} monster{xpCalc.totalMonsters !== 1 ? "s" : ""} vs {partySize} players (lvl {partyLevel})
                    </p>
                  </div>
                </div>
                {/* XP Threshold Bar */}
                <div className="mt-3 flex gap-1 text-[10px]">
                  {["easy", "medium", "hard", "deadly"].map(d => {
                    const active = xpCalc.adjustedXP >= xpCalc.thresholds[d];
                    return (
                      <div key={d} className={`flex-1 text-center py-1 rounded-sm ${active ? DIFFICULTY_STYLES[d].bg : "bg-muted/20"}`}>
                        <span className={active ? DIFFICULTY_STYLES[d].color : "text-muted-foreground"}>
                          {d.charAt(0).toUpperCase() + d.slice(1)}: {xpCalc.thresholds[d].toLocaleString()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Creature Lineup */}
            <div className="space-y-2">
              {encounterCreatures.length === 0 ? (
                <Card className="bg-card border-border border-dashed rounded-sm">
                  <CardContent className="p-8 text-center">
                    <Skull className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
                    <p className="text-muted-foreground text-sm">Add creatures from the Bestiary panel</p>
                    <p className="text-muted-foreground text-xs mt-1">Click a creature to add it to this encounter</p>
                  </CardContent>
                </Card>
              ) : (
                encounterCreatures.map(ec => {
                  const xp = CR_XP[ec.challenge_rating] || 0;
                  return (
                    <Card key={ec.creature_id} className="bg-card border-border rounded-sm" data-testid={`encounter-creature-${ec.creature_id}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Skull className="w-4 h-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-foreground truncate">{ec.name}</h4>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> CR {ec.challenge_rating}</span>
                              {ec.hit_points && <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {ec.hit_points} HP</span>}
                              <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {(xp * ec.quantity).toLocaleString()} XP</span>
                            </div>
                          </div>
                          {/* Quantity controls */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => updateQuantity(ec.creature_id, -1)} data-testid={`dec-qty-${ec.creature_id}`}>
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-6 text-center text-sm font-bold text-foreground" data-testid={`qty-${ec.creature_id}`}>{ec.quantity}</span>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => updateQuantity(ec.creature_id, 1)} data-testid={`inc-qty-${ec.creature_id}`}>
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive flex-shrink-0" onClick={() => removeCreature(ec.creature_id)} data-testid={`remove-creature-${ec.creature_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>

            {/* Run Button */}
            {encounterCreatures.length > 0 && (
              <div className="mt-4">
                <Button onClick={runInCombat} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="run-encounter-button">
                  <Play className="w-4 h-4 mr-2" /> Run in Combat Tracker
                </Button>
              </div>
            )}

            {/* Notes */}
            <div className="mt-4">
              <Input
                value={planNotes}
                onChange={(e) => { setPlanNotes(e.target.value); setDirty(true); }}
                placeholder="Encounter notes (terrain, tactics, etc.)..."
                className="bg-input border-border rounded-sm text-sm"
                data-testid="plan-notes-input"
              />
            </div>
          </div>

          {/* ==================== RIGHT PANEL: PARTY + BESTIARY ==================== */}
          <div className="lg:col-span-4 space-y-4">
            {/* Party Config */}
            <Card className="bg-card border-border rounded-sm">
              <CardContent className="p-4">
                <h3 className="text-sm font-medium text-foreground flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-primary" /> Party Configuration
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Party Size</label>
                    <Input
                      type="number" min={1} max={10}
                      value={partySize}
                      onChange={(e) => { setPartySize(Math.max(1, parseInt(e.target.value) || 1)); setDirty(true); }}
                      className="bg-input border-border rounded-sm"
                      data-testid="party-size-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Avg Level</label>
                    <Input
                      type="number" min={1} max={20}
                      value={partyLevel}
                      onChange={(e) => { setPartyLevel(Math.max(1, Math.min(20, parseInt(e.target.value) || 1))); setDirty(true); }}
                      className="bg-input border-border rounded-sm"
                      data-testid="party-level-input"
                    />
                  </div>
                </div>
                {characters.filter(c => c.character_type === "pc").length > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Auto-detected from {characters.filter(c => c.character_type === "pc").length} campaign PCs
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Bestiary Panel */}
            <Card className="bg-card border-border rounded-sm">
              <CardContent className="p-4">
                <button className="w-full flex items-center justify-between mb-3" onClick={() => setBestiaryOpen(!bestiaryOpen)}>
                  <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Skull className="w-4 h-4 text-primary" /> Bestiary
                    <span className="text-xs text-muted-foreground">({creatures.length})</span>
                  </h3>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${bestiaryOpen ? "rotate-180" : ""}`} />
                </button>

                {bestiaryOpen && (
                  <>
                    <div className="flex gap-2 mb-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <Input
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Search..."
                          className="pl-8 h-8 text-xs bg-input border-border rounded-sm"
                          data-testid="bestiary-search"
                        />
                      </div>
                      <Select value={filterType} onValueChange={setFilterType}>
                        <SelectTrigger className="w-28 h-8 text-xs bg-input border-border rounded-sm" data-testid="bestiary-filter">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          <SelectItem value="all">All Types</SelectItem>
                          {CREATURE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {creatures.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-xs text-muted-foreground">No creatures in your Bestiary</p>
                        <Button variant="link" size="sm" className="text-xs text-primary mt-1" onClick={() => navigate(`/campaign/${campaignId}/creatures`)}>
                          Go to Bestiary
                        </Button>
                      </div>
                    ) : filteredCreatures.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-4">No matches</p>
                    ) : (
                      <div className="space-y-1 max-h-[50vh] overflow-y-auto pr-1">
                        {filteredCreatures.map(creature => {
                          const inEncounter = encounterCreatures.find(c => c.creature_id === creature.id);
                          return (
                            <div
                              key={creature.id}
                              className={`p-2 rounded-sm cursor-pointer flex items-center gap-2 transition-colors ${
                                inEncounter ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/30 border border-transparent"
                              }`}
                              onClick={() => addCreature(creature)}
                              data-testid={`bestiary-creature-${creature.id}`}
                            >
                              <Skull className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span className="text-sm text-foreground truncate block">{creature.name}</span>
                                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                                  <span>CR {creature.challenge_rating || "?"}</span>
                                  {creature.creature_type && <span className="capitalize">{creature.creature_type}</span>}
                                  <span>{(CR_XP[creature.challenge_rating] || 0).toLocaleString()} XP</span>
                                </div>
                              </div>
                              {inEncounter ? (
                                <span className="text-xs text-primary font-bold">{inEncounter.quantity}x</span>
                              ) : (
                                <Plus className="w-3.5 h-3.5 text-muted-foreground" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ==================== SAVE DIALOG ==================== */}
        <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Save Encounter Plan</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Name</label>
                <Input
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                  placeholder="Goblin Ambush"
                  className="bg-input border-border rounded-sm"
                  data-testid="save-plan-name-input"
                />
              </div>
              <div className="text-sm text-muted-foreground">
                {encounterCreatures.length} creature type{encounterCreatures.length !== 1 ? "s" : ""} ({xpCalc.totalMonsters} total)
                &mdash; <span className={diffStyle.color}>{diffStyle.label}</span> difficulty
              </div>
              <Button onClick={savePlan} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="confirm-save-plan">
                {activePlan ? "Update Plan" : "Save Plan"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      </TierGate>
    </Layout>
  );
};

export default EncounterBuilderPage;
